export type PricingCurrency = 'EUR';
export type PricingVersion = 'v1';
export type BookingRateType = 'normal' | 'special' | 'free_child';

export interface PricingRate {
  rateId: string;
  label: string;
  unitPrice: number;
  enabled: boolean;
}

export interface NormalPricingRate extends PricingRate {
  rateId: 'normal';
  enabled: true;
}

export interface FreeChildRule {
  enabled: boolean;
  rateId: 'free_child_under_age';
  label: string;
  maxAgeExclusive: number;
  unitPrice: 0;
}

export type SpecialPricingRate = PricingRate;

export interface PricingConfig {
  currency: PricingCurrency;
  normalRate: NormalPricingRate;
  freeChildRule: FreeChildRule;
  specialRates: SpecialPricingRate[];
  version: PricingVersion;
}

export interface BookingSelection {
  rateId: string;
  quantity: number;
}

export interface BookingLine {
  rateId: string;
  labelSnapshot: string;
  unitPriceSnapshot: number;
  rateType: BookingRateType;
  quantityInitial: number;
  quantityActive: number;
  quantityCancelled: number;
  lineAmountInitial: number;
  lineAmountCurrent: number;
}

export interface BookingParticipantTotals {
  initialParticipants: number;
  activeParticipants: number;
  cancelledParticipants: number;
}

export interface BookingMoneyTotals {
  initialTotal: number;
  currentTotalDue: number;
  totalPaid: number;
  totalRefunded: number;
  totalRefundable: number;
  totalOutstanding: number;
}

export interface PricingCalculationResult {
  pricingSnapshot: PricingConfig;
  bookingLines: BookingLine[];
  participantTotals: BookingParticipantTotals;
  moneyTotals: BookingMoneyTotals;
}

export interface BookingLike {
  pricingSnapshot?: PricingConfig;
  bookingLines: BookingLine[];
  moneyTotals?: Partial<BookingMoneyTotals>;
  totalPaid?: number;
  totalRefunded?: number;
}

type ResolvedRate = {
  rateId: string;
  label: string;
  unitPrice: number;
  enabled: boolean;
  rateType: BookingRateType;
};

function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

function clonePricingConfig(pricingConfig: PricingConfig): PricingConfig {
  return {
    currency: pricingConfig.currency,
    normalRate: { ...pricingConfig.normalRate },
    freeChildRule: { ...pricingConfig.freeChildRule },
    specialRates: pricingConfig.specialRates.map((rate) => ({ ...rate })),
    version: pricingConfig.version,
  };
}

function cloneBookingLine(line: BookingLine): BookingLine {
  return { ...line };
}

function normalizeLabel(label: string): string {
  return String(label || '').trim();
}

function normalizeRateId(rateId: string): string {
  return String(rateId || '').trim();
}

function validateStrictRateId(rateId: unknown, message: string): string {
  assert(typeof rateId === 'string', message);
  assert(rateId.length > 0, message);
  assert(rateId === normalizeRateId(rateId), message);
  return rateId;
}

function validateFiniteNumber(value: unknown, message: string): number {
  assert(isFiniteNumber(value), message);
  return value;
}

function validatePositiveNumber(value: unknown, message: string): number {
  const numericValue = validateFiniteNumber(value, message);
  assert(numericValue > 0, message);
  return numericValue;
}

function validateNonNegativeNumber(value: unknown, message: string): number {
  const numericValue = validateFiniteNumber(value, message);
  assert(numericValue >= 0, message);
  return numericValue;
}

function validateNonEmptyLabel(label: string, message: string): string {
  const normalizedLabel = normalizeLabel(label);
  assert(normalizedLabel.length > 0, message);
  return normalizedLabel;
}

function getResolvedRates(pricingConfig: PricingConfig): ResolvedRate[] {
  return [
    {
      rateId: pricingConfig.normalRate.rateId,
      label: pricingConfig.normalRate.label,
      unitPrice: pricingConfig.normalRate.unitPrice,
      enabled: pricingConfig.normalRate.enabled,
      rateType: 'normal',
    },
    {
      rateId: pricingConfig.freeChildRule.rateId,
      label: pricingConfig.freeChildRule.label,
      unitPrice: pricingConfig.freeChildRule.unitPrice,
      enabled: pricingConfig.freeChildRule.enabled,
      rateType: 'free_child',
    },
    ...pricingConfig.specialRates.map((rate) => ({
      rateId: rate.rateId,
      label: rate.label,
      unitPrice: rate.unitPrice,
      enabled: rate.enabled,
      rateType: 'special' as const,
    })),
  ];
}

function getResolvedRateMap(pricingConfig: PricingConfig): Map<string, ResolvedRate> {
  return new Map(getResolvedRates(pricingConfig).map((rate) => [rate.rateId, rate]));
}

function sumBookingLineParticipants(
  bookingLines: BookingLine[],
  key: 'quantityInitial' | 'quantityActive' | 'quantityCancelled',
): number {
  return bookingLines.reduce((sum, line) => sum + line[key], 0);
}

function sumBookingLineAmounts(bookingLines: BookingLine[], key: 'lineAmountInitial' | 'lineAmountCurrent'): number {
  return roundCurrency(bookingLines.reduce((sum, line) => sum + line[key], 0));
}

function resolvePaidAndRefundedAmounts(bookingLike: BookingLike): { totalPaid: number; totalRefunded: number } {
  const totalPaidSource = bookingLike.moneyTotals?.totalPaid ?? bookingLike.totalPaid ?? 0;
  const totalRefundedSource = bookingLike.moneyTotals?.totalRefunded ?? bookingLike.totalRefunded ?? 0;

  return {
    totalPaid: roundCurrency(validateNonNegativeNumber(totalPaidSource, 'totalPaid doit être un nombre fini >= 0.')),
    totalRefunded: roundCurrency(validateNonNegativeNumber(totalRefundedSource, 'totalRefunded doit être un nombre fini >= 0.')),
  };
}

export function validatePricingConfig(pricingConfig: PricingConfig): PricingConfig {
  assert(pricingConfig && typeof pricingConfig === 'object', 'pricingConfig est requis.');
  assert(pricingConfig.currency === 'EUR', 'La devise doit être EUR.');
  assert(pricingConfig.version === 'v1', 'La version pricing doit être v1.');
  assert(pricingConfig.normalRate && typeof pricingConfig.normalRate === 'object', 'normalRate est requis.');
  assert(pricingConfig.normalRate.rateId === 'normal', 'normalRate.rateId doit être normal.');
  assert(pricingConfig.normalRate.enabled === true, 'normalRate.enabled doit être true.');
  validateNonEmptyLabel(pricingConfig.normalRate.label, 'normalRate.label est requis.');
  validatePositiveNumber(pricingConfig.normalRate.unitPrice, 'normalRate.unitPrice doit être > 0.');

  assert(pricingConfig.freeChildRule && typeof pricingConfig.freeChildRule === 'object', 'freeChildRule est requis.');
  assert(typeof pricingConfig.freeChildRule.enabled === 'boolean', 'freeChildRule.enabled doit être un booléen.');
  assert(pricingConfig.freeChildRule.rateId === 'free_child_under_age', 'freeChildRule.rateId doit être free_child_under_age.');
  validateNonEmptyLabel(pricingConfig.freeChildRule.label, 'freeChildRule.label est requis.');
  assert(pricingConfig.freeChildRule.unitPrice === 0, 'freeChildRule.unitPrice doit être 0.');
  validateFiniteNumber(pricingConfig.freeChildRule.maxAgeExclusive, 'freeChildRule.maxAgeExclusive doit être un nombre fini.');
  if (pricingConfig.freeChildRule.enabled) {
    assert(pricingConfig.freeChildRule.maxAgeExclusive > 0, 'freeChildRule.maxAgeExclusive doit être > 0 si la règle est activée.');
  }

  assert(Array.isArray(pricingConfig.specialRates), 'specialRates doit être un tableau.');
  assert(pricingConfig.specialRates.length <= 3, 'specialRates ne peut pas contenir plus de 3 tarifs.');

  const seenRateIds = new Set<string>();
  for (const rate of getResolvedRates(pricingConfig)) {
    const rateId = validateStrictRateId(rate.rateId, 'Tous les rateId doivent être non vides et déjà normalisés.');
    assert(!seenRateIds.has(rateId), `rateId dupliqué: ${rateId}`);
    seenRateIds.add(rateId);
    validateNonEmptyLabel(rate.label, `Le label du tarif ${rateId} est requis.`);

    if (rate.rateType !== 'free_child') {
      assert(typeof rate.enabled === 'boolean', `Le statut enabled du tarif ${rateId} doit être un booléen.`);
    }

    if (rate.rateType === 'free_child') {
      assert(rate.unitPrice === 0, 'Le tarif enfant gratuit doit être à 0.');
    } else {
      validatePositiveNumber(rate.unitPrice, `Le prix du tarif ${rateId} doit être > 0.`);
    }
  }

  return pricingConfig;
}

export function validateBookingPricingSelection(
  pricingConfig: PricingConfig,
  selection: BookingSelection[],
): BookingSelection[] {
  validatePricingConfig(pricingConfig);
  assert(Array.isArray(selection) && selection.length > 0, 'La sélection tarifaire ne peut pas être vide.');

  const resolvedRates = getResolvedRateMap(pricingConfig);
  const seenRateIds = new Set<string>();
  let totalParticipants = 0;

  for (const line of selection) {
    assert(line && typeof line === 'object', 'Chaque ligne de sélection est requise.');
    const rateId = validateStrictRateId(line.rateId, 'Chaque ligne de sélection doit avoir un rateId non vide et déjà normalisé.');
    assert(!seenRateIds.has(rateId), `rateId dupliqué dans la sélection: ${rateId}`);
    seenRateIds.add(rateId);
    assert(Number.isInteger(line.quantity), `La quantité du tarif ${rateId} doit être un entier.`);
    assert(line.quantity > 0, `La quantité du tarif ${rateId} doit être > 0.`);

    const resolvedRate = resolvedRates.get(rateId);
    assert(resolvedRate, `Tarif inconnu: ${rateId}`);
    assert(resolvedRate.enabled, `Tarif désactivé: ${rateId}`);

    totalParticipants += line.quantity;
  }

  assert(totalParticipants > 0, 'Le total des participants doit être > 0.');
  return selection;
}

export function calculateBookingPricing(
  pricingConfig: PricingConfig,
  selection: BookingSelection[],
): PricingCalculationResult {
  validateBookingPricingSelection(pricingConfig, selection);

  const pricingSnapshot = clonePricingConfig(pricingConfig);

  const bookingLines = getResolvedRates(pricingSnapshot)
    .filter((rate) => selection.some((item) => item.rateId === rate.rateId))
    .map((rate) => {
      const selectionLine = selection.find((item) => item.rateId === rate.rateId);
      const quantity = selectionLine?.quantity || 0;
      const lineAmount = roundCurrency(rate.unitPrice * quantity);

      return {
        rateId: rate.rateId,
        labelSnapshot: rate.label,
        unitPriceSnapshot: rate.unitPrice,
        rateType: rate.rateType,
        quantityInitial: quantity,
        quantityActive: quantity,
        quantityCancelled: 0,
        lineAmountInitial: lineAmount,
        lineAmountCurrent: lineAmount,
      } satisfies BookingLine;
    });

  const participantTotals = {
    initialParticipants: sumBookingLineParticipants(bookingLines, 'quantityInitial'),
    activeParticipants: sumBookingLineParticipants(bookingLines, 'quantityActive'),
    cancelledParticipants: 0,
  } satisfies BookingParticipantTotals;

  const initialTotal = sumBookingLineAmounts(bookingLines, 'lineAmountInitial');
  const moneyTotals = {
    initialTotal,
    currentTotalDue: initialTotal,
    totalPaid: 0,
    totalRefunded: 0,
    totalRefundable: 0,
    totalOutstanding: initialTotal,
  } satisfies BookingMoneyTotals;

  return {
    pricingSnapshot,
    bookingLines,
    participantTotals,
    moneyTotals,
  };
}

export function calculateCurrentBookingTotals(bookingLike: BookingLike): PricingCalculationResult {
  assert(bookingLike && typeof bookingLike === 'object', 'bookingLike est requis.');
  assert(bookingLike.pricingSnapshot, 'pricingSnapshot est requis.');
  validatePricingConfig(bookingLike.pricingSnapshot);
  assert(Array.isArray(bookingLike?.bookingLines), 'bookingLines est requis.');

  const bookingLines = bookingLike.bookingLines.map((line) => {
    assert(line && typeof line === 'object', 'Chaque bookingLine est requise.');
    const rateId = validateStrictRateId(line.rateId, 'Chaque bookingLine doit avoir un rateId non vide et déjà normalisé.');
    const quantityInitial = validateNonNegativeNumber(line.quantityInitial, `quantityInitial invalide pour ${rateId}.`);
    const quantityActive = validateNonNegativeNumber(line.quantityActive, `quantityActive invalide pour ${rateId}.`);
    const quantityCancelled = validateNonNegativeNumber(line.quantityCancelled, `quantityCancelled invalide pour ${rateId}.`);
    assert(Number.isInteger(quantityInitial), `quantityInitial invalide pour ${rateId}.`);
    assert(Number.isInteger(quantityActive), `quantityActive invalide pour ${rateId}.`);
    assert(Number.isInteger(quantityCancelled), `quantityCancelled invalide pour ${rateId}.`);
    const unitPriceSnapshot = validateFiniteNumber(line.unitPriceSnapshot, `unitPriceSnapshot invalide pour ${rateId}.`);
    const lineAmountInitial = validateNonNegativeNumber(line.lineAmountInitial, `lineAmountInitial invalide pour ${rateId}.`);
    const lineAmountCurrent = validateNonNegativeNumber(line.lineAmountCurrent, `lineAmountCurrent invalide pour ${rateId}.`);
    assert(quantityInitial === quantityActive + quantityCancelled, `Les quantités de ${line.rateId} sont incohérentes.`);
    assert(normalizeLabel(line.labelSnapshot).length > 0, `labelSnapshot invalide pour ${rateId}.`);
    assert(['normal', 'special', 'free_child'].includes(line.rateType), `rateType invalide pour ${rateId}.`);

    if (line.rateType === 'free_child') {
      assert(unitPriceSnapshot === 0, `unitPriceSnapshot invalide pour ${rateId}.`);
    } else {
      assert(unitPriceSnapshot > 0, `unitPriceSnapshot invalide pour ${rateId}.`);
    }

    assert(lineAmountInitial === roundCurrency(unitPriceSnapshot * quantityInitial), `lineAmountInitial incohérent pour ${rateId}.`);
    assert(lineAmountCurrent === roundCurrency(unitPriceSnapshot * quantityActive), `lineAmountCurrent incohérent pour ${rateId}.`);

    return {
      ...cloneBookingLine(line),
      lineAmountInitial,
      lineAmountCurrent,
    };
  });

  const participantTotals = {
    initialParticipants: sumBookingLineParticipants(bookingLines, 'quantityInitial'),
    activeParticipants: sumBookingLineParticipants(bookingLines, 'quantityActive'),
    cancelledParticipants: sumBookingLineParticipants(bookingLines, 'quantityCancelled'),
  } satisfies BookingParticipantTotals;

  const initialTotal = sumBookingLineAmounts(bookingLines, 'lineAmountInitial');
  const currentTotalDue = sumBookingLineAmounts(bookingLines, 'lineAmountCurrent');
  const { totalPaid, totalRefunded } = resolvePaidAndRefundedAmounts(bookingLike);
  const totalRefundable = roundCurrency(Math.max(totalPaid - currentTotalDue - totalRefunded, 0));
  const totalOutstanding = roundCurrency(Math.max(currentTotalDue - totalPaid, 0));

  return {
    pricingSnapshot: clonePricingConfig(bookingLike.pricingSnapshot),
    bookingLines,
    participantTotals,
    moneyTotals: {
      initialTotal,
      currentTotalDue,
      totalPaid,
      totalRefunded,
      totalRefundable,
      totalOutstanding,
    },
  };
}

export function applyPartialCancellationByRate(
  bookingLike: BookingLike,
  cancellationByRate: Record<string, number>,
): PricingCalculationResult {
  assert(cancellationByRate && typeof cancellationByRate === 'object', 'cancellationByRate est requis.');
  const normalizedBooking = calculateCurrentBookingTotals(bookingLike);

  const updatedLines = normalizedBooking.bookingLines.map((line) => {
    const requestedCancellation = cancellationByRate[line.rateId];
    if (requestedCancellation === undefined) {
      return cloneBookingLine(line);
    }

    assert(Number.isInteger(requestedCancellation), `La quantité à annuler pour ${line.rateId} doit être un entier.`);
    assert(requestedCancellation > 0, `La quantité à annuler pour ${line.rateId} doit être > 0.`);
    assert(requestedCancellation <= line.quantityActive, `Impossible d'annuler plus que la quantité active pour ${line.rateId}.`);

    const quantityActive = line.quantityActive - requestedCancellation;
    const quantityCancelled = line.quantityCancelled + requestedCancellation;

    return {
      ...cloneBookingLine(line),
      quantityActive,
      quantityCancelled,
      lineAmountCurrent: roundCurrency(line.unitPriceSnapshot * quantityActive),
    };
  });

  return calculateCurrentBookingTotals({
    pricingSnapshot: normalizedBooking.pricingSnapshot,
    bookingLines: updatedLines,
    moneyTotals: {
      totalPaid: normalizedBooking.moneyTotals.totalPaid,
      totalRefunded: normalizedBooking.moneyTotals.totalRefunded,
    },
  });
}

export function calculateRefundableAmount(bookingLike: BookingLike): number {
  return calculateCurrentBookingTotals(bookingLike).moneyTotals.totalRefundable;
}

export function buildPaymentAmount(bookingLike: BookingLike): number {
  return calculateCurrentBookingTotals(bookingLike).moneyTotals.totalOutstanding;
}